import {useRef, useEffect} from 'react';
import {createPortal} from 'react-dom';
import {UserMinimal} from '@types';
import {useTheme} from '@components/theme/useTheme';
import {replaceAvatarUrlWithTheme} from '@utils/user';
import {useTranslation} from 'react-i18next';

type UsernameSuggestionsProps = {
    suggestions: UserMinimal[];
    selectedIndex: number;
    onSuggestionClick: (suggestion: UserMinimal) => void;
    setSelectedIndex: (index: number) => void;
    position: {top: number; left: number};
    isLoading: boolean;
};

export const UsernameSuggestions = ({
    suggestions,
    selectedIndex,
    onSuggestionClick,
    setSelectedIndex,
    position,
    isLoading,
}: UsernameSuggestionsProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Scroll selected item into view within the dropdown container
    useEffect(() => {
        const container = containerRef.current;
        const item = itemRefs.current[selectedIndex];
        if (selectedIndex < 0 || !container || !item) return;

        const itemTop = item.offsetTop;
        const itemBottom = itemTop + item.offsetHeight;
        const scrollTop = container.scrollTop;
        const viewBottom = scrollTop + container.clientHeight;

        if (itemBottom > viewBottom) {
            container.scrollTo({
                top: itemBottom - container.clientHeight,
                behavior: 'smooth',
            });
        } else if (itemTop < scrollTop) {
            container.scrollTo({top: itemTop, behavior: 'smooth'});
        }
    }, [selectedIndex]);

    if (!suggestions.length && !isLoading) {
        return null;
    }

    return createPortal(
        <div
            ref={containerRef}
            className="absolute z-[9999] bg-body border border-neutral-300 rounded-md shadow-lg max-h-40 overflow-y-auto min-w-48"
            style={{
                top: position.top,
                left: position.left,
            }}>
            {isLoading ? (
                <div className="px-3 py-2 text-neutral-500 text-sm">
                    {t('common.searching')}
                </div>
            ) : (
                suggestions.map((suggestion, index) => (
                    <div
                        key={suggestion.id}
                        ref={el => {
                            itemRefs.current[index] = el;
                        }}
                        className={`cursor-pointer p-2 transition-colors ${
                            index === selectedIndex ? 'bg-body-tertiary' : ''
                        }`}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            onSuggestionClick(suggestion);
                        }}
                        onMouseEnter={() => {
                            setSelectedIndex(index);
                        }}>
                        <div className="flex items-center">
                            {suggestion.avatar && (
                                <img
                                    src={replaceAvatarUrlWithTheme(
                                        suggestion.avatar.sources.find(
                                            source => source.size === 512
                                        )?.url ||
                                            suggestion.avatar.sources[0]?.url ||
                                            '',
                                        theme
                                    )}
                                    className="rounded-full !w-5 !h-5 mr-1.5"
                                    alt={`${suggestion.name} avatar`}
                                />
                            )}
                            <div className="flex font-semibold leading-normal text-sm">
                                <div className="max-w-36 overflow-x-hidden whitespace-nowrap overflow-ellipsis">
                                    @{suggestion.name}
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>,
        document.body
    );
};
